import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import axios from 'axios';
import GetCookie from '../../utils/GetCookie';
import { StatusBadgeXSm } from '../../utils/StatusBadge';
import PaginationControls from '../../utils/PaginationControls';
import TrimContent from '../../utils/TrimContent';
import authNavigate from '../../globalComponents/AuthNav';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';

const ExtractionModelList = forwardRef(({ onOpenModel, onEditModel, onRefresh, openModelIds, onDeleteModel, onError, fabricIdFromSLR, embedded = false }, ref) => {
  const [models, setModels] = useState([]);
  const [expandedFabrics, setExpandedFabrics] = useState({});
  const [expandedModels, setExpandedModels] = useState({});
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [downloadingFabricId, setDownloadingFabricId] = useState(null);

  const navigate = useNavigate();

  const API_BASE_URL = '/pceapi/textractor';
  const headers = {
    Authorization: GetCookie('access_token')
  };

  const fetchModels = async () => {
    setIsLoading(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/list-models`,
        {
          page,
          size,
          search: search || undefined,
          fabric_id: fabricIdFromSLR || null
        },
        { headers }
      );

      const data = response.data;

      if (data.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return;
      }

      if (data.status === 'failed') {
        onError(data.message || 'Failed to load sheets');
        return;
      }

      setModels(data.data || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Error fetching models:', error);
      onError(error.message || 'Failed to load sheets');
    } finally {
      setIsLoading(false);
    }
  };

  // Expose fetchModels to parent component via ref
  useImperativeHandle(ref, () => ({
    fetchModels
  }));

  /* ── Download all sheets of a workbook as a single Excel file ── */
  const handleDownloadWorkbook = async (fabric) => {
    if (!fabric.models || fabric.models.length === 0) return;
    setDownloadingFabricId(fabric.id);
    try {
      const wb = XLSX.utils.book_new();
      const completedModels = fabric.models.filter(m => m.status === 'COMPLETED');
      for (const model of completedModels) {
        const res = await axios.post(
          `${API_BASE_URL}/download-model`,
          { table_id: model.id },
          { headers, responseType: 'blob' }
        );
        const csvText = await res.data.text();
        const ws = XLSX.read(csvText, { type: 'string' }).Sheets['Sheet1']
          ?? XLSX.utils.aoa_to_sheet(
            csvText.split('\n').map(row => row.split(','))
          );
        const sheetName = (model.table_name || `Sheet_${model.id}`).slice(0, 31);
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      }
      const fileName = `${fabric.name}_workbook.xlsx`.replace(/[^\w\s.-]/g, '_');
      XLSX.writeFile(wb, fileName);
    } catch (err) {
      console.error('Workbook download failed:', err);
      onError?.('Failed to download workbook');
    } finally {
      setDownloadingFabricId(null);
    }
  };

  // Fetch models when component mounts or when pagination/search changes
  useEffect(() => {
    fetchModels();
  }, [page, size, search,fabricIdFromSLR]);

  // Toggle fabric expansion
  const toggleFabricExpansion = (fabricId) => {
    setExpandedFabrics(prev => ({
      ...prev,
      [fabricId]: !prev[fabricId]
    }));
  };

  // Toggle model expansion
  const toggleModelExpansion = (modelId) => {
    setExpandedModels(prev => ({
      ...prev,
      [modelId]: !prev[modelId]
    }));
  };

  // Handle search input change
  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
  };

  // Handle search button click
  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1); // Reset to first page on search
  };

  // Handle Enter key press in search input
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="sidebar-inner ev-card-2">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <p className="ev-reg-p-dark ev-bold-reg mb-0">
          Workbooks {!embedded && <span> ({total} Projects)</span>}
        </p>
      </div>
      
      <div className="d-flex justify-content-between">
        <div className="mb-2 col-8 position-relative">
          <input
            type="text"
            className="form-control search-input ms-0"
            placeholder="Search Sheets"
            value={searchInput}
            onChange={handleSearchInputChange}
            onKeyPress={handleKeyPress}
          />
          <button 
            className="ev-btn-lightblue-fill-round search-bar-button" 
            onClick={handleSearch}
          >
            <i className="bi bi-search"></i>
          </button>
        </div>
        <div className="gap2 mt-1"> 
          <button 
              className="ev-btn-lightblue-fill-round ev-bold-reg ms-2" 
              onClick={fetchModels}
              disabled={isLoading}
            >
              <i className="bi bi-arrow-repeat"></i> Refresh 
          </button>
        </div>
      </div>

      <div className="tree-container mt-2 scrollable-element">
        
        {isLoading && (
          <div className="text-center p-3 mt-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}
        
        {!isLoading && models.length === 0 && (
          <div className="text-center p-3">
            <p className="ev-small-p-blue">No sheet found</p>
          </div>
        )}
        
        {!isLoading && models.length > 0 && (
          <div className="tree-container-inner">
            {models.map(fabric => (
              <div key={fabric.id} className="ev-card-1 ev-padding-small mb-2 ev-radius-small">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <button 
                      className="btn btn-sm p-0 me-2 ev-fs-20" 
                      onClick={() => toggleFabricExpansion(fabric.id)}
                    >
                      <i className={`ev-reg-p-dark bi ${expandedFabrics[fabric.id] ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
                    </button>                        
                    <span className="ev-small-p-blue avatar-tooltip ev-bold-med" data-tooltip={fabric.name}>
                      <i className="bi bi-boxes"></i> &nbsp;
                      <TrimContent text={fabric.name} maxLength={25} />
                      &nbsp;
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <span className="ev-message-card2 ev-radius-med ev-fs-11 ev-bold-med">
                      {fabric.model_count} Sheets
                    </span>
                    <button
                      className="ev-card-6 ev-btn-small"
                      onClick={(e) => { e.stopPropagation(); handleDownloadWorkbook(fabric); }}
                      disabled={downloadingFabricId === fabric.id || !fabric.models?.some(m => m.status === 'COMPLETED')}
                      title="Download all sheets as Excel"
                    >
                      {downloadingFabricId === fabric.id
                        ? <span className="spinner-border spinner-border-sm" role="status" />
                        : <i className="ev-bold-reg bi bi-download"></i>
                      }
                    </button>
                  </div>
                </div>
                
                {expandedFabrics[fabric.id] && (
                  <div>
                    <p className="ev-small-p-blue mb-1 mt-3 ev-bold-med">Sheets</p>
                    {fabric.models.length === 0 ? (
                      <div className="ev-card-2 mt-2">
                        <p className="ev-small-p-blue text-center mb-0">No sheet in this project</p>
                      </div>
                    ) : (
                      fabric.models.map(model => (
                        <div key={model.id} className="ev-card-9 mt-2 ev-radius-small">
                            <div className="text-end">
                              <button 
                                className={`ev-btn-blue-fill-round ev-btn-small ev-bold-reg ${openModelIds.includes(model.id) ? 'disabled' : ''}`}
                                onClick={() => onOpenModel(model, fabric.id, fabric.name)}
                                disabled={openModelIds.includes(model.id)}
                              >
                                {openModelIds.includes(model.id) ? <><i className="bi bi-dash-circle" />{' '}Opened</> : <><i className="bi bi-plus-circle" />{' '}Open Sheet</>}
                              </button>
                              
                              <button 
                                className="ev-btn-skyblue-fill-round ev-radius-xsmall pe-2 ps-2 ms-2 avatar-tooltip"
                                onClick={() => onEditModel(model, fabric.id, fabric.name)}
                                data-tooltip={model.status === 'RUNNING' ? 'unning' : 'Edit Sheet'}
                                disabled={model.status === 'RUNNING'}
                              >
                                <i className="bi bi-pencil"></i>
                              </button>
                              
                              <button 
                                className="ev-btn-red-fill-round ev-radius-xsmall pe-2 ps-2 ms-2 avatar-tooltip"
                                onClick={() => onDeleteModel(model.id, fabric.id, model.table_name)}
                                data-tooltip={model.status === 'RUNNING' ? 'Running' : 'Delete Sheet'}
                                disabled={model.status === 'RUNNING'} 
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>
                            <div className="d-flex align-items-center justify-content-between mb-1 mt-2">
                              <div className="d-flex align-items-center flex-grow-1 me-2">
                                <button 
                                  className="btn btn-sm p-0 me-2 ev-fs-20" 
                                  onClick={() => toggleModelExpansion(model.id)}
                                >
                                  <i className={`ev-reg-p-dark bi ${expandedModels[model.id] ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
                                </button>
                                <span className="ev-small-p-dark avatar-tooltip" data-tooltip={model.table_name}> 
                                  <i className="bi bi-bricks"></i> 
                                  &nbsp;
                                  <TrimContent text={model.table_name} maxLength={20} />
                                  &nbsp;
                                </span> 
                              </div> 
                              <span className="flex-shrink-0"> 
                                <StatusBadgeXSm status={model.status} />
                              </span>     
                            </div>
                                             
                            {/* Show columns when model is expanded */}
                            {expandedModels[model.id] && (
                              <div className="mt-0 p-2">
                                <p className="ev-small-p-blue mb-0 ev-bold-med">
                                  Columns
                                </p>
                                
                                {model.columns && model.columns.length > 0 ? (
                                      <div>
                                        {model.columns.map((column, idx) => (
                                          <p className="ev-message-card2 ev-fs-10  w-100 mb-1" key={idx}>
                                            <span className ="ev-bold-med">
                                              {column.name}
                                               <TrimContent text={column.name} maxLength={20} />
                                              </span> ({column.datatype})
                                          </p>
                                        ))}
                                      </div>
                                ) : (
                                  <p className="ev-small-p-blue text-center mb-0">No columns defined</p>
                                )}
                                <div className="text-end"> 
                                <p className="ev-message-card3 ev-small-p-blue mb-0">
                                  <i className="bi bi-file-earmark-text"></i> Documents: {model.document_count || 0}
                                </p>
                                </div>
                              </div>
                            )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Pagination for sidebar */}
      <div className="d-flex justify-content-between align-items-center mt-3 sidebar-inner-bottom-fix-ele ev-fs-12">
        <div>
          <button 
            className="ev-btn-light-fill ev-btn-xsmall" 
            disabled={page === 1 || isLoading}
            onClick={() => setPage(page - 1)}
          >
            <i className="bi bi-chevron-left"></i>
          </button>
          
          <span className="ev-xsmall-p-dark ms-2">
            Page {page} of {Math.ceil(total / size) || 1}
          </span>
          
          <button 
            className="ev-btn-light-fill ev-btn-xsmall ms-2" 
            disabled={page >= Math.ceil(total / size) || isLoading}
            onClick={() => setPage(page + 1)}
          >
            <i className="bi bi-chevron-right"></i>
          </button>
        </div>
        <div>
         <PaginationControls 
              initialSize={size}
              onSizeChange={(newSize) => {
                setSize(newSize);
                setPage(1);
              }}
            />
        </div>
      </div>
    </div>
  );
});

// Add display name for better debugging
ExtractionModelList.displayName = 'ExtractionModelList';

export default ExtractionModelList;